/**
 * Geofence API Service
 * Handles CRUD operations for geofences
 */

export interface GeoJSONGeometry {
  type: 'Polygon';
  coordinates: number[][][]; // [[[lng, lat], [lng, lat], ...]]
}

export interface Geofence {
  id: number;
  name: string;
  description?: string;
  geometry: GeoJSONGeometry;
  created_by?: string;
  created_at: string;
  updated_at: string;
  course_id?: string;
  active: boolean;
}

export interface GeofenceCreate {
  name: string;
  description?: string;
  geometry?: GeoJSONGeometry;
  geojson_polygon?: any; // Support both formats
  course_id?: string;
}

export interface DeviceInGeofence {
  device_id: string;
  entity_id?: string;
  device_type?: string;
  latitude: number;
  longitude: number;
  last_seen_minutes_ago?: number;
}

export interface GeofenceWithDevices {
  geofence: Geofence;
  devices: DeviceInGeofence[];
  device_count: number;
}

class GeofenceService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
  }

  /**
   * Create a new geofence
   */
  async createGeofence(geofence: GeofenceCreate): Promise<Geofence> {
    const response = await fetch(`${this.baseUrl}/geofences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
      },
      body: JSON.stringify(geofence)
    });

    if (!response.ok) {
      let errorDetail = response.statusText;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorData.message || response.statusText;
        console.error('[GeofenceService] Error creating geofence:', errorData);
      } catch (e) {
        // Ignore parse error
      }
      throw new Error(`Failed to create geofence: ${errorDetail}`);
    }

    return await response.json();
  }

  /**
   * List all geofences
   */
  async getGeofences(courseId?: string, activeOnly: boolean = true): Promise<Geofence[]> {
    return this.listGeofences(courseId, activeOnly);
  }

  /**
   * List all geofences (internal)
   */
  async listGeofences(courseId?: string, activeOnly: boolean = true): Promise<Geofence[]> {
    const params = new URLSearchParams();
    if (courseId) params.append('course_id', courseId);
    params.append('active_only', activeOnly.toString());

    const url = `${this.baseUrl}/geofences?${params.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        let errorDetail = response.statusText;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || response.statusText;
          console.error('[GeofenceService] ❌ Error listing geofences:', errorData);
        } catch (e) {
          // Ignore parse error
        }
        throw new Error(`Failed to list geofences (${response.status}): ${errorDetail}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[GeofenceService] ❌ Fetch error:', error);
      throw error;
    }
  }

  /**
   * Delete a geofence
   */
  async deleteGeofence(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/geofences/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete geofence: ${response.statusText}`);
    }
  }

  /**
   * Get devices within a geofence
   */
  async getDevicesInGeofence(id: number): Promise<DeviceInGeofence[]> {
    const url = `${this.baseUrl}/geofences/${id}/devices`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
      }
    });
    
    if (!response.ok) {
      console.error(`[GeofenceService] ❌ Failed to get devices for geofence ${id}: ${response.status}`);
      throw new Error(`Failed to get devices in geofence: ${response.statusText}`);
    }

    const data = await response.json();
    // Handle both direct array response and wrapped response
    let devices: DeviceInGeofence[] = [];
    if (Array.isArray(data)) {
      devices = data;
    } else if (data.devices) {
      devices = data.devices;
    }
    
    return devices;
  }
}

export const geofenceService = new GeofenceService();

